// @ts-nocheck
import * as React from 'react';
import Icon from '../../components/common/Icon';
import { PageHero, Section, SendBtn, PresentBtn, SentBadge } from '../shared/InternalShared';

// childcare.jsx — AI Childcare Counselling. Accent: pink.
// "Prevent early. Protect always."

import { useEffect, useState } from "react";
import { fetchChildcareItems } from '../services/internalContentApi';

const CC_STAGES = [
  { id: "ec",  name: "Early Childhood", age: "2–5", issues: ["Separation Anxiety", "Speech Delays"],     out: ["Security", "Stability"],       fix: "Gentle settling-in routines and parent-coaching audio that ease the first goodbye." },
  { id: "lp",  name: "Lower Primary",   age: "6–8", issues: ["Homework Fear", "Bullying"],               out: ["Confidence", "Adaptability"],  fix: "Confidence-building stories and a quiet protocol for naming and reporting bullying." },
  { id: "up",  name: "Upper Primary",   age: "9–11", issues: ["Low Self-Esteem", "Screen Attachment"],   out: ["Self-Belief", "Focus"],        fix: "Self-worth practice and a family screen-balance plan that actually holds." },
  { id: "ms",  name: "Middle School",   age: "12–14", issues: ["Peer Pressure", "Mood Swings"],          out: ["Emotional Control", "Clarity"], fix: "Emotion-regulation tools and scripts for the conversations that suddenly matter." },
  { id: "sec", name: "Secondary",       age: "15–16", issues: ["Board Stress", "Anxiety"],               out: ["Resilience", "Discipline"],    fix: "Calm-under-pressure routines tuned to the first high-stakes board year." },
  { id: "ss",  name: "Senior Secondary", age: "17–18", issues: ["Exam Pressure", "Burnout"],             out: ["Mental Strength", "Stability"], fix: "Burnout early-warning and recovery rhythms for the JEE / NEET final push." },
];

export default function PageChildcare({ student, accent, sendToParent, onContentSelect }) {
  // Aarav is 8 → Lower Primary
  const ageStage = CC_STAGES.find((s) => {
    const [lo, hi] = s.age.split("–").map((n) => parseInt(n));
    return student.childAge >= lo && student.childAge <= hi;
  }) || CC_STAGES[1];
  const [sel, setSel] = useState(ageStage.id);
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [preview, setPreview] = useState(null);
  const [contentState, setContentState] = useState({ loading: true, error: '' });
  const active = CC_STAGES.find((s) => s.id === sel);
  const first = student.child.split(" ")[0];
  const selectedContent = items.find((item, index) => `${item.CATEGORY}-${item.ISSUE}-${index}` === selectedItem) || items[0] || null;
  useEffect(() => { onContentSelect?.(selectedContent); }, [selectedContent, onContentSelect]);

  useEffect(() => {
    let current = true;
    setContentState({ loading: true, error: '' });
    setSelectedItem(null);
    setPreview(null);
    fetchChildcareItems({ age: student.childAge, gender: student.gender })
      .then((data) => { if (current) { setItems(data); if (data[0]) setSelectedItem(`${data[0].CATEGORY}-${data[0].ISSUE}-0`); } })
      .catch((error) => { if (current) { setItems([]); setContentState({ loading: false, error: error?.message || 'Unable to load childcare content.' }); } })
      .finally(() => { if (current) setContentState((state) => ({ ...state, loading: false })); });
    return () => { current = false; };
  }, [student.childAge, student.gender]);

  const heroRight = (
    <div className="ip-card ip-rise" style={{ padding: 22, background: "var(--bg)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div className="ip-stat__n">44<sup>+</sup></div>
          <div className="ip-stat__l">Challenges solved</div>
        </div>
        <div>
          <div className="ip-stat__n">6</div>
          <div className="ip-stat__l">Developmental stages</div>
        </div>
        <div>
          <div className="ip-stat__n" style={{ color: "var(--accent)" }}>0</div>
          <div className="ip-stat__l">Missed early signs</div>
        </div>
      </div>
      <hr className="ip-hr" style={{ margin: "18px 0" }} />
      <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
        <div className="ip-iconbox" style={{ width: 34, height: 34, borderRadius: 9 }}><Icon name="shield" size={17} /></div>
        <div style={{ fontSize: 12.5, color: "var(--fg-secondary)", lineHeight: 1.5 }}>
          AI watches for the early signs of <strong style={{ color: "var(--fg)" }}>{ageStage.issues.join(" and ").toLowerCase()}</strong> — the quiet risks of {first}'s stage — and acts before they affect learning.
        </div>
      </div>
    </div>
  );

  return (
    <div className="ip" style={{ "--accent": accent }}>
      <PageHero
        eyebrow="AI Childcare Counselling"
        icon="parent"
        title={<>Prevent early.<br/><span className="ip-accent">Protect always.</span></>}
        lead="Forty-four childhood challenges, anticipated and softened with AI — so emotional wellbeing is never the reason a bright child falls behind."
        actions={<>
          <SendBtn onClick={() => sendToParent("Childcare wellbeing plan", selectedContent)} label="Send wellbeing plan" />
          <PresentBtn onClick={() => sendToParent("Childcare walkthrough")} />
        </>}
        trust={{ label: "Aligned with", items: [
          { label: "NCERT" },
          { label: "AICTE" },
          { label: "UGC" },
        ] }}
        right={heroRight}
      />

      <Section
        eyebrow="The childhood map"
        title="Every stage has a different fear"
        hint={`Highlighted for ${first} · ${student.grade} · age ${student.childAge}.`}
      >
        <div className="ip-card" style={{ padding: "16px 18px 6px", position: "relative" }}>
          <div className="ip-stepline" />
          <div className="ip-stepper">
            {CC_STAGES.map((s) => (
              <button key={s.id} className={"ip-step" + (sel === s.id ? " is-on" : "")} onClick={() => setSel(s.id)}>
                <div className="ip-step__node" />
                <div className="ip-step__age">Age {s.age}</div>
                <div className="ip-step__name">{s.name}</div>
              </button>
            ))}
          </div>
        </div>

        {/* selected stage detail */}
        <div className="ip-grid ip-grid--2 ip-rise" key={active.id} style={{ marginTop: 14 }}>
          <div className="ip-card" style={{ padding: 22 }}>
            <span className="ip-tag ip-tag--mute" style={{ background: "var(--amber-soft)", color: "var(--amber)" }}>Risks at this stage</span>
            <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
              {active.issues.map((iss) => (
                <span key={iss} style={{ fontSize: 13.5, fontWeight: 700, color: "var(--fg)", padding: "8px 14px", borderRadius: 10, background: "var(--bg-inset)", border: "1px solid var(--border)" }}>{iss}</span>
              ))}
            </div>
            <div style={{ fontSize: 13, color: "var(--fg-secondary)", marginTop: 16, lineHeight: 1.6 }}>
              <strong style={{ color: "var(--fg)" }}>How AI helps · </strong>{active.fix}
            </div>
          </div>
          <div className="ip-card" style={{ padding: 22, display: "flex", flexDirection: "column", justifyContent: "center", background: "color-mix(in srgb, var(--accent) 4%, var(--bg))" }}>
            <span className="ip-eyebrow">We build toward</span>
            <div style={{ display: "flex", gap: 28, marginTop: 16 }}>
              {active.out.map((o) => (
                <div key={o} style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <Icon name="check" size={18} stroke={2.4} style={{ color: "var(--accent)" }} />
                  <span style={{ fontSize: 17, fontWeight: 700, color: "var(--fg)", letterSpacing: "-0.01em" }}>{o}</span>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 12.5, color: "var(--fg-tertiary)", marginTop: 16, lineHeight: 1.5 }}>
              Outcomes a parent can feel at home — and a school can see in the classroom.
            </div>
          </div>
        </div>
      </Section>

      <Section
        eyebrow="Beyond the classroom"
        title="Six pressures modern children carry"
        hint={`Personalised for ${first} · age ${student.childAge}`}
        style={{ paddingTop: 4 }}
      >
        <ContentState state={contentState} empty={!items.length} />
        <div className="ip-grid ip-grid--3">
          {items.map((item, index) => {
            const key = `${item.CATEGORY}-${item.ISSUE}-${index}`;
            const selected = selectedItem === key;
            return (
            <article key={key} className={`ip-card ip-card--hover ip-api-card${selected ? ' is-selected' : ''}`} onClick={() => setSelectedItem(key)} tabIndex={0} role="button" aria-pressed={selected} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setSelectedItem(key); } }}>
              <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                <div className="ip-iconbox" style={{ width: 34, height: 34, borderRadius: 9 }}><Icon name="shield" size={17} /></div>
                <span className="ip-tag">{item.CATEGORY}</span>
                {item.IS_SENT ? <SentBadge /> : null}
              </div>
              <div className="ip-api-card__message">{item.ISSUE}</div>
              <div className="ip-preview-actions">
                {item.AUDIO_URL ? <button type="button" className="ip-btn ip-btn--ghost ip-btn--sm" onClick={(event) => { event.stopPropagation(); setSelectedItem(key); setPreview({ type: 'audio', url: item.AUDIO_URL, title: item.ISSUE }); }}>Audio preview</button> : null}
                {item.VIDEO_URL ? <button type="button" className="ip-btn ip-btn--ghost ip-btn--sm" onClick={(event) => { event.stopPropagation(); setSelectedItem(key); setPreview({ type: 'video', url: item.VIDEO_URL, title: item.ISSUE }); }}>Video preview</button> : null}
              </div>
            </article>);
          })}
        </div>
      </Section>

      {preview ? <MediaPreview preview={preview} onClose={() => setPreview(null)} /> : null}

      <div className="ip-band" style={{ "--accent": accent }}>
        <span className="ip-band__eyebrow">Prevention, not cure</span>
        <div className="ip-band__title">When the mind is steady, results follow.</div>
        <div className="ip-band__grid">
          {[
            { n: "↓ Anxiety", l: "Reduced exam anxiety" },
            { n: "Resilience", l: "Strong emotional resilience" },
            { n: "Discipline", l: "Consistent study discipline" },
            { n: "Clarity", l: "Mental clarity under pressure" },
            { n: "JEE / NEET", l: "Competitive readiness" },
          ].map((s) => (
            <div key={s.l}>
              <div className="ip-band__stat-n">{s.n}</div>
              <div className="ip-band__stat-l">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ContentState({ state, empty }) {
  if (state.loading) return <CardSkeletonGrid />;
  if (state.error) return <div className="ip-api-state" role="alert">{state.error}</div>;
  if (empty) return <div className="ip-api-state">No content is available for this student yet.</div>;
  return null;
}

function CardSkeletonGrid() {
  return <div className="ip-card-skeleton-grid" role="status" aria-label="Loading personalised content">{Array.from({ length: 6 }).map((_, index) => <div className="ip-card ip-card-skeleton" key={index}><span className="ip-skeleton ip-card-skeleton__icon" /><span className="ip-skeleton ip-card-skeleton__tag" /><span className="ip-skeleton ip-card-skeleton__line" /><span className="ip-skeleton ip-card-skeleton__line ip-card-skeleton__line--short" /><span className="ip-skeleton ip-card-skeleton__action" /></div>)}</div>;
}

function MediaPreview({ preview, onClose }) {
  return <div className="ip-preview-backdrop" role="dialog" aria-modal="true" aria-label={`${preview.type} preview`} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}><div className={`ip-preview-modal ip-preview-modal--${preview.type}`}><div className="ip-preview-modal__head"><div className="ip-preview-modal__identity"><span className="ip-preview-modal__type">{preview.type}</span><strong>{preview.title}</strong><small>Personalised content preview</small></div><button type="button" onClick={onClose} aria-label="Close preview">×</button></div><div className="ip-preview-modal__stage">{preview.type === 'audio' ? <><div className="ip-preview-modal__audio-art"><Icon name="voice" size={30} /></div><audio controls autoPlay src={preview.url} /></> : <video controls autoPlay playsInline src={preview.url} />}</div><div className="ip-preview-modal__foot"><span>Preview only</span><small>Use the player controls to adjust volume or playback.</small></div></div></div>;
}




